/**
 * The live half of the form-first flow: what arrives on the stream that no persisted
 * row says yet.
 *
 * A sibling of `reduceResearch`, for the same reason: none of this belongs to the
 * turn. Driver options come from a background job, concepts arrive one at a time
 * while their run is still going, and "the script is saved" is a fact about the
 * draft. `TurnState` resets on every settle, which would wipe all three.
 *
 * Everything here is a hint layered over the durable transcript — a reload loses it
 * and the persisted rows (concept cards, the submitted answers) carry on regardless.
 * That is why nothing here is ever the only place a fact lives.
 */
import type { LiveOptions } from "./answers/form/formValues";
import type { Concept } from "./answers/form/formTypes";
import type { AgentStreamEvent } from "./types";

export type LiveConcepts = {
  /** The run that is producing this set; a new run replaces the set. */
  runId: string | null;
  items: Concept[];
  total: number | null;
  completed: boolean;
};

export type FormLiveState = {
  /** Keyed by `optionsKey(formId, fieldKey)`. */
  options: Record<string, LiveOptions>;
  concepts: LiveConcepts;
  scriptReady: boolean;
};

export const optionsKey = (formId: string, fieldKey: string): string =>
  `${formId}:${fieldKey}`;

export const initialFormLiveState: FormLiveState = {
  options: {},
  concepts: { runId: null, items: [], total: null, completed: false },
  scriptReady: false,
};

/** Run ids are increasing integers, sent as strings. */
const isOlderRun = (runId: string, than: string): boolean =>
  Number(runId) < Number(than);

/** Companion to `reduceTurn`: returns the same reference when nothing changed. */
export const reduceFormLive = (
  state: FormLiveState,
  event: AgentStreamEvent,
): FormLiveState => {
  switch (event.type) {
    case "FORM_OPTIONS": {
      if (!event.formId || !event.fieldKey) return state;
      const key = optionsKey(event.formId, event.fieldKey);
      const existing = state.options[key];
      // Runs are answered in whatever order the model finishes: an older run's options
      // arriving after a newer run's must not replace them.
      if (existing && isOlderRun(event.runId, existing.runId)) return state;
      return {
        ...state,
        options: {
          ...state.options,
          [key]: {
            options: event.options ?? [],
            status: event.optionsStatus ?? "ready",
            seq: (existing?.seq ?? 0) + 1,
            runId: event.runId,
            productId: event.productId,
            url: event.url,
          },
        },
      };
    }

    case "CONCEPT_READY": {
      if (!event.concept) return state;
      const { concept } = event;
      // A new run is a new set — "Generar otros" must not append to the old one.
      const fresh = state.concepts.runId !== event.runId;
      const items = fresh ? [] : state.concepts.items;
      if (items.some((item) => item.id === concept.id)) return state;
      return {
        ...state,
        concepts: {
          runId: event.runId,
          items: [...items, concept],
          total: event.total,
          completed: false,
        },
      };
    }

    case "CONCEPTS_COMPLETED":
      if (state.concepts.completed) return state;
      return { ...state, concepts: { ...state.concepts, completed: true } };

    case "SCRIPT_READY":
      if (state.scriptReady) return state;
      return { ...state, scriptReady: true };

    default:
      return state;
  }
};
